import axios from 'axios'
import clsx from 'clsx'
import {isEmpty} from 'lodash'
import {FC, useEffect, useMemo, useState} from 'react'
import {useIntl} from 'react-intl'
import {
  Button,
  DropdownButton,
  Table,
  TableBody,
  TableHeader,
  TableWrapper,
  ValidationErrorModal,
} from '../../../../_gori/partials/widgets'
import {KTSVG} from '../../../../_gori/helpers'
import {
  OrganizationAccountsFilter,
  OrganizationAccountsFilterValues,
} from '../modals/OrganizationAccountsFilter'
import {AssignManagerModal} from '../modals/AssignManagerModal'
import {
  buildActiveFilterChips,
  hasActiveAccountFilters,
  mapManagersToAssignOptions,
  removeFilterChip,
} from '../helpers/organizationAccountsFilterHelpers'
import {TableSkeleton} from '../../../../_gori/helpers/components'
import {TABLE_KEY_COLUMN} from '../../../../_gori/constants'
import {isActiveEnterpriseManager, isEnterpriseOwner, useAuth} from '../../auth'
import {convertCurrency, convertUserTimeZone} from '../../../../_gori/helpers/ConvertHelpers'
import {EmptyTable} from '../../common/components/EmptyTable'
import useCancelToken from '../../../../_gori/hooks/UseCancelToken'
import OrganizationService from '../core/_requests'
import UserService from '../../users/core/_requests'
import {toast} from 'react-toastify'
import type {OrganizationAccount, OrganizationAccountsPagination} from '../core/_types'

type OrganizationAccountsProps = {
  tableData: OrganizationAccount[]
  pagination: Partial<OrganizationAccountsPagination>
  loadingFirst: boolean
  accountFilters: OrganizationAccountsFilterValues | null
  onAccountFiltersChange: (filters: OrganizationAccountsFilterValues | null) => void
  onReloadAccounts: () => void | Promise<void>
}

const OrganizationAccounts: FC<OrganizationAccountsProps> = ({
  tableData,
  pagination,
  loadingFirst,
  accountFilters,
  onAccountFiltersChange,
  onReloadAccounts,
}) => {
  const [selectedList, setSelectedList] = useState<any>([])
  const intl = useIntl()
  const {loadingSwitch, currentUser, handleSaveCurrentUser, setLoadingSwitch} = useAuth()
  const {isCancel, newCancelToken} = useCancelToken()
  const [validationErrors, setValidationErrors] = useState<any>()
  const [showAccountsFilter, setShowAccountsFilter] = useState(false)
  const [showAssignManagerModal, setShowAssignManagerModal] = useState(false)
  const [assignTargetAccountIds, setAssignTargetAccountIds] = useState<number[]>([])
  const [managerOptions, setManagerOptions] = useState<Array<{value: string; label: string}>>([])
  const signatureAccessKey = process.env.REACT_APP_SIGNATURE_ACCESS_KEY

  const canAssignManager = currentUser && isEnterpriseOwner(currentUser)

  useEffect(() => {
    setSelectedList([])
  }, [tableData])

  const tableSelectionKey = useMemo(() => tableData.map((row) => row.id).join(','), [tableData])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const openAssignManagerModal = (accountIds: number[]) => {
    if (!accountIds.length || !currentUser || !isEnterpriseOwner(currentUser)) {
      return
    }

    const loadManagersAssignable = async () => {
      try {
        const response = await OrganizationService.getManagersAssignable({
          cancelToken: newCancelToken(),
        })
        setManagerOptions(mapManagersToAssignOptions(response.managers))
        setAssignTargetAccountIds(accountIds)
        setShowAssignManagerModal(true)
      } catch (error: any) {
        if (isCancel(error)) return
        setValidationErrors(error?.response)
      }
    }

    loadManagersAssignable()
  }

  const handleSaveAssignManager = async (manager: {value: string; label: string} | null) => {
    if (!manager || !currentUser || !isEnterpriseOwner(currentUser)) {
      return false
    }

    try {
      await OrganizationService.assignManager(
        manager.value,
        {
          account_ids: assignTargetAccountIds,
        },
        {
          cancelToken: newCancelToken(),
        }
      )

      await onReloadAccounts()
      setAssignTargetAccountIds([])
      toast.success(intl.formatMessage({id: 'UPDATED_SUCCESSFULLY'}))
      return true
    } catch (error: any) {
      if (isCancel(error)) return false
      setValidationErrors(error?.response)
      return false
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSwitchToAccount = async (accountId: number | string) => {
    if (!currentUser || loadingSwitch) {
      return
    }

    const nextAccount = currentUser.accounts?.find(
      (account) => String(account.id) === String(accountId)
    )
    if (!nextAccount || !signatureAccessKey) {
      return
    }

    axios.defaults.headers.common[signatureAccessKey] = nextAccount.encrypt_key
    setLoadingSwitch(true)

    try {
      const user = await UserService.getUser()
      handleSaveCurrentUser(user)
    } catch (_error) {
      // Keep behavior aligned with SwitchCompany: fail silently.
    } finally {
      setLoadingSwitch(false)
    }
  }

  const columns = useMemo(
    () => [
      {
        id: TABLE_KEY_COLUMN.CAN_CHECK,
        accessor: 'canCheck',
      },
      {
        id: 'account_name',
        Header: intl.formatMessage({id: 'ACCOUNT_NAME'}),
        headerClassName: 'min-w-150px text-center',
        cellClassName: 'min-w-150px text-dark text-center',
        Cell: ({row}: {row: any}) => {
          return <div>{row.original.name}</div>
        },
      },
      {
        id: 'id',
        Header: intl.formatMessage({id: 'ACCOUNT_ID'}),
        headerClassName: 'min-w-150px text-center',
        cellClassName: 'min-w-150px text-dark text-center',
        Cell: ({row}: {row: any}) => {
          return <div>{row.original.id}</div>
        },
      },
      {
        id: 'created_at',
        Header: intl.formatMessage({id: 'CREATION_DATE'}),
        headerClassName: 'min-w-150px text-center',
        cellClassName: 'min-w-150px text-dark text-center',
        Cell: ({row}: {row: any}) => {
          return convertUserTimeZone(
            row.original?.created_at,
            currentUser,
            process.env.REACT_APP_DATE_FORMAT
          )
        },
      },
      {
        id: 'manager',
        Header: intl.formatMessage({id: 'MANAGER'}),
        headerClassName: 'min-w-150px text-center',
        cellClassName: 'min-w-150px text-dark text-center text-wrap',
        Cell: ({row}: {row: any}) => {
          return <div>{row.original.manager_name}</div>
        },
      },
      {
        id: 'branch',
        Header: intl.formatMessage({id: 'BRANCH'}),
        headerClassName: 'min-w-150px text-center',
        cellClassName: 'min-w-150px text-dark text-center text-wrap',
        Cell: ({row}: {row: any}) => {
          return <div>{row.original.branch_name}</div>
        },
      },
      {
        id: 'status',
        Header: intl.formatMessage({id: 'STATUS'}),
        accessor: 'status',
        headerClassName: 'w-150px text-center',
        cellClassName: 'w-150px text-center',
        Cell: ({row}: {row: any}) => {
          const status = row.original?.status?.value === 1 ? 'active' : 'disabled'
          return (
            <span
              className={`account-status-badge-${status} px-4 py-2 rounded-pill w-100 d-flex justify-content-center`}
            >
              {row.original?.status?.label}
            </span>
          )
        },
      },
      {
        id: 'last_shipment_date',
        Header: intl.formatMessage({id: 'LAST_SHIPMENT_DATE'}),
        headerClassName: 'min-w-200px text-center',
        cellClassName: 'min-w-200px text-dark text-center',
        Cell: ({row}: {row: any}) => {
          return convertUserTimeZone(
            row.original?.last_shipment_date,
            currentUser,
            process.env.REACT_APP_DATE_FORMAT
          )
        },
      },
      {
        id: 'shipment_volume',
        Header: intl.formatMessage({id: 'SHIPMENT_VOLUME'}),
        headerClassName: 'min-w-150px text-center',
        cellClassName: 'min-w-150px text-dark text-center',
        Cell: ({row}: {row: any}) => {
          return <div>{row.original.shipment_volume?.toLocaleString()}</div>
        },
      },
      {
        id: 'total_spend',
        Header: intl.formatMessage({id: 'TOTAL_SPEND'}),
        headerClassName: 'min-w-150px text-center',
        cellClassName: 'text-dark text-center',
        Cell: ({row}: {row: any}) => {
          return <div>{convertCurrency(row.original.total_spend)}</div>
        },
      },
      {
        id: TABLE_KEY_COLUMN.ACTIONS,
        Header: intl.formatMessage({id: 'ACTIONS'}),
        cellClassName: 'fixed-column',
        Cell: ({row}: {row: any}) => {
          const listDropDownBtn = [
            {
              label: intl.formatMessage({id: 'ASSIGN_MANAGER'}),
              action: () => {
                openAssignManagerModal([row.original.id])
              },
              className: 'cursor-pointer',
              isVisible: currentUser && isEnterpriseOwner(currentUser),
            },
            {
              label: intl.formatMessage({id: 'GO_TO_ACCOUNT'}),
              action: () => {
                handleSwitchToAccount(row.original.id)
              },
              className: 'cursor-pointer',
              isVisible:
                currentUser &&
                (isEnterpriseOwner(currentUser) || isActiveEnterpriseManager(currentUser)),
            },
          ]

          return (
            <DropdownButton
              loading={row.original.isLoading}
              list={listDropDownBtn.filter((item) => item.isVisible)}
              className='d-flex justify-content-center'
            />
          )
        },
      },
    ],
    [currentUser, handleSwitchToAccount, intl, openAssignManagerModal]
  )

  const hasActiveFilters = hasActiveAccountFilters(accountFilters)
  const activeFilterChips = useMemo(
    () => (accountFilters ? buildActiveFilterChips(accountFilters, intl) : []),
    [accountFilters, intl]
  )

  const handleRemoveFilterChip = (chipId: typeof activeFilterChips[number]['id']) => {
    if (!accountFilters) {
      return
    }

    const nextFilters = removeFilterChip(accountFilters, chipId)
    onAccountFiltersChange(hasActiveAccountFilters(nextFilters) ? {...nextFilters} : null)
  }

  const handleClearAllFilters = () => {
    onAccountFiltersChange(null)
  }

  const handleApplyFilters = (filters: OrganizationAccountsFilterValues) => {
    onAccountFiltersChange(hasActiveAccountFilters(filters) ? {...filters} : null)
  }

  return (
    <>
      {validationErrors && (
        <ValidationErrorModal
          handleClose={() => setValidationErrors(undefined)}
          response={validationErrors}
        />
      )}
      <AssignManagerModal
        show={showAssignManagerModal}
        handleClose={() => {
          setShowAssignManagerModal(false)
          setAssignTargetAccountIds([])
        }}
        onSave={handleSaveAssignManager}
        managerOptions={managerOptions}
      />
      <OrganizationAccountsFilter
        show={showAccountsFilter}
        handleClose={() => setShowAccountsFilter(false)}
        initialValues={accountFilters ?? undefined}
        onApply={handleApplyFilters}
      />
      {loadingFirst ? (
        <TableWrapper className='rounded-top-left-0-dt'>
          <div className='table-content--inset-inner h-100'>
            <div className='organization-accounts-filter-skeleton my-5 mb-8'>
              <div className='organization-accounts-filter-skeleton__btn placeholder-wave' />
            </div>
            <TableBody>
              <TableSkeleton />
            </TableBody>
          </div>
        </TableWrapper>
      ) : (
        <>
          <TableWrapper className='rounded-top-left-0-dt'>
            <div className='table-content--inset-inner h-100'>
              <TableHeader className='min-h-1px my-5 mb-8'>
                <div className='organization-accounts-active-filters d-flex flex-wrap align-items-center gap-3 w-100'>
                  <Button
                    className={clsx(
                      'organization-accounts-active-filters__filter-btn d-flex flex-row-reverse btn border',
                      {
                        'organization-accounts-active-filters__filter-btn--active':
                          hasActiveFilters,
                      }
                    )}
                    event={() => setShowAccountsFilter(true)}
                    label={intl.formatMessage({id: 'FILTER'})}
                    icon={
                      <KTSVG
                        path='/media/gori/orders/filter.svg'
                        className='svg-icon svg-icon-1 ms-3'
                        small={false}
                      />
                    }
                  />
                  {hasActiveFilters && (
                    <>
                      <div className='organization-accounts-active-filters__chips d-flex flex-wrap align-items-center gap-2'>
                        {activeFilterChips.map((chip) => (
                          <div key={chip.id} className='organization-accounts-active-filters__chip'>
                            <span className='organization-accounts-active-filters__chip-label'>
                              {chip.label}
                            </span>
                            <span className='organization-accounts-active-filters__chip-value'>
                              {chip.value}
                            </span>
                            <Button
                              className='btn btn-sm btn-icon p-0 border-0 bg-transparent justify-content-end filter-selected-badge__remove-btn'
                              aria-label={intl.formatMessage({id: 'REMOVE'})}
                              event={() => handleRemoveFilterChip(chip.id)}
                            >
                              <KTSVG
                                path='/media/gori/a1/close.svg'
                                className='svg-icon svg-icon-4'
                              />
                            </Button>
                          </div>
                        ))}
                        <div className='organization-accounts-active-filters__actions d-flex align-items-center gap-4'>
                          <button
                            type='button'
                            className='organization-accounts-active-filters__clear-all'
                            onClick={handleClearAllFilters}
                          >
                            <span>{intl.formatMessage({id: 'CLEAR_ALL'})}</span>
                            <KTSVG
                              path='/media/gori/a1/close.svg'
                              className='svg-icon svg-icon-4'
                            />
                          </button>
                          <button
                            type='button'
                            className='organization-accounts-active-filters__edit-filter'
                            onClick={() => setShowAccountsFilter(true)}
                          >
                            {intl.formatMessage({id: 'EDIT_FILTER'})}
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
                {canAssignManager && (
                  <div className='card-toolbar flex-row-fluid justify-content-start align-items-center mt-5'>
                    <div
                      className={`d-flex align-items-center ${
                        !(selectedList.length > 0) && 'd-none'
                      }`}
                    >
                      <div className='fw-bolder me-5'>
                        <span className='me-2'>{selectedList.length}</span>
                        {intl.formatMessage({id: 'SELECTED'})}
                      </div>
                      <Button
                        className='btn bg-light-success bg-hover-success text-hover-white btn-sm fs-5 me-3'
                        style={{color: '#0B6D65', borderRadius: '8px'}}
                        label={intl.formatMessage({id: 'ASSIGN_MANAGER'})}
                        loadingText={intl.formatMessage({id: 'ASSIGN_MANAGER'})}
                        event={() =>
                          openAssignManagerModal(selectedList.map((item: any) => item.original.id))
                        }
                      />
                    </div>
                  </div>
                )}
              </TableHeader>
              <TableBody>
                {isEmpty(tableData) ? (
                  <EmptyTable />
                ) : (
                  <Table
                    key={tableSelectionKey}
                    columns={columns}
                    data={tableData}
                    pagination={pagination}
                    tbodyClass='text-gray-600 fw-bold'
                    usePagination
                    useCheckBox={canAssignManager}
                    callBackSetData={(selected) => {
                      setSelectedList(selected)
                    }}
                  />
                )}
              </TableBody>
            </div>
          </TableWrapper>
        </>
      )}
    </>
  )
}

export {OrganizationAccounts}
